"use client";

import { useState, useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

type TimeRange = "30d" | "90d" | "1y" | "all";

interface ChainTvlChartProps {
  data: Array<{ date: string; tvl: number }>;
}

function formatTvl(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

export function ChainTvlChart({ data }: ChainTvlChartProps) {
  const [range, setRange] = useState<TimeRange>("90d");

  const filteredData = useMemo(() => {
    if (range === "all") return data;
    const now = Date.now();
    const days = range === "30d" ? 30 : range === "90d" ? 90 : 365;
    const cutoff = now - days * 86400 * 1000;
    return data.filter((d) => new Date(d.date).getTime() >= cutoff);
  }, [data, range]);

  const isPositive = useMemo(() => {
    if (filteredData.length < 2) return true;
    return filteredData[filteredData.length - 1]!.tvl >= filteredData[0]!.tvl;
  }, [filteredData]);

  const gradientColor = isPositive ? "#10b981" : "#ef4444";
  const strokeColor = isPositive ? "#10b981" : "#ef4444";

  const ranges: { key: TimeRange; label: string }[] = [
    { key: "30d", label: "30D" },
    { key: "90d", label: "90D" },
    { key: "1y", label: "1Y" },
    { key: "all", label: "All" },
  ];

  return (
    <div>
      {/* Time range toggles */}
      <div className="flex items-center gap-1 mb-4">
        {ranges.map((r) => (
          <button
            key={r.key}
            onClick={() => setRange(r.key)}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              range === r.key
                ? "bg-blue-600 text-white"
                : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={filteredData}>
            <defs>
              <linearGradient id="tvlGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={gradientColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={gradientColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tickFormatter={(d: string) => {
                const date = new Date(d);
                return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              }}
              stroke="#4b5563"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              minTickGap={40}
            />
            <YAxis
              tickFormatter={formatTvl}
              stroke="#4b5563"
              tick={{ fill: "#6b7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={70}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(230,15%,12%)",
                border: "1px solid hsl(230,15%,20%)",
                borderRadius: "8px",
                color: "#fff",
                fontSize: "13px",
              }}
              labelFormatter={(d) =>
                new Date(String(d)).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })
              }
              formatter={(value) => [formatTvl(Number(value)), "TVL"]}
            />
            <Area
              type="monotone"
              dataKey="tvl"
              stroke={strokeColor}
              strokeWidth={2}
              fill="url(#tvlGradient)"
              dot={false}
              animationDuration={800}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
